import React, {
  useCallback, useEffect, useState, memo, useMemo,
} from 'react';
import {connect, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {ActionSignOut} from '../../store/actions/actions-user';
import {USER_ROLE} from '../../constants/user';
import {ActionGetNewVideoPollsCounter} from '../../store/actions/actions-pages';
import {ExitIcon} from '../SvgComponents/SvgComponents';
import {
  guestLinks, candidatesLinks, employerLinks, adminLinks,
  assistantLinks, assistantAdminLinks, directorLinks, founderLinks,
} from './nav-items';
import ModalConfirm from '../Modal/newTemplates/ModalConfirm';
import SidebarNavItem from './SidebarNavItem';
import {t} from '../../services/i18nHandler';

const {
  CANDIDATE, EMPLOYER, RECRUITER, ASSISTANT, ADMIN_ASSISTANT, ADMINISTRATOR, DIRECTOR, FOUNDER,
} = USER_ROLE;

function SidebarNav({
  pathname, user, newVideoPolls, loggedIn, exitLoading,
}) {
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  const [newVideoPollCount, setNewVideoPollCount] = useState(0);

  const userRole = useMemo(() => (user.role), [user]);

  const dispatch = useDispatch();

  const isActive = useMemo(() => (route = '') => pathname.includes(route), [pathname]);

  const onConfirmExit = useCallback(() => {
    dispatch(ActionSignOut());
  }, []);

  const getVideoPollCounter = useCallback(() => {
    if (loggedIn) {
      dispatch(ActionGetNewVideoPollsCounter());
    }
  }, [loggedIn]);

  const linksArray = useMemo(() => {
    if (!loggedIn) {
      return guestLinks;
    }
    switch (userRole) {
    case CANDIDATE: return candidatesLinks;
    case EMPLOYER:
    case RECRUITER: return employerLinks;
    case ASSISTANT: return assistantLinks;
    case ADMIN_ASSISTANT: return assistantAdminLinks;
    case DIRECTOR: return directorLinks;
    case FOUNDER: return founderLinks;
    case ADMINISTRATOR: return adminLinks;
    default: return guestLinks;
    }
  }, [user, userRole, pathname, newVideoPollCount, isActive, loggedIn]);

  const getItemQuantity = useMemo(() => (quantityKey) => {
    if (quantityKey === 'newVideoPollCount') {
      return newVideoPollCount;
    }
    return false;
  }, [newVideoPollCount]);

  const onExitEvent = useCallback(() => {
    if (showConfirmExit && !loggedIn) {
      setShowConfirmExit(false);
    }
  }, [showConfirmExit, loggedIn]);

  const links = useMemo(() => (linksArray.sort((a, b) => (a.disabled - b.disabled))), [linksArray]);

  useEffect(() => {
    setNewVideoPollCount(newVideoPolls);
  }, [newVideoPolls]);

  useEffect(() => {
    getVideoPollCounter();
  }, [pathname, loggedIn]);

  useEffect(() => {
    onExitEvent();
  }, [loggedIn]);

  return (
    <div className="sidebar__nav custom-scroll-small">
      {showConfirmExit && (
        <ModalConfirm
          className="modal-confirm-small"
          onClose={() => setShowConfirmExit(false)}
          onConfirm={onConfirmExit}
          isOpen
          text={t('common.exitText')}
          confirmBtnText="buttons.logOut"
          textCenter
          loading={exitLoading}
        />
      )}
      <ul className="sidebar__nav__list">
        {links.map((linkItem, index) => {
          const active = linkItem.route && isActive(linkItem.route);
          const quantity = getItemQuantity(linkItem.quantityKey);
          return (
            <SidebarNavItem
              key={index}
              data={linkItem}
              active={active}
              quantity={quantity}
            />
          );
        })}
        {loggedIn && (
          <li className="sidebar-nav_item" onClick={() => setShowConfirmExit(true)}>
            <div className="sidebar-nav_link_wrap">
              <div className="sidebar-nav_link">
                <div className="sidebar-nav_item-icon">
                  <ExitIcon />
                </div>
                <span>{t('nav.logOut')}</span>
              </div>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
}


SidebarNav.defaultProps = {
  newVideoPolls: 0,
  loggedIn: false,
  exitLoading: false,
};

SidebarNav.propTypes = {
  user: PropTypes.shape({}).isRequired,
  pathname: PropTypes.string.isRequired,
  newVideoPolls: PropTypes.number,
  loggedIn: PropTypes.bool,
  exitLoading: PropTypes.bool,
};

function mapStateToProps({
  rUser, router, rServerData, rSession, rLoading,
}) {
  return {
    user: rUser,
    pathname: router.location.pathname,
    newVideoPolls: rServerData.newVideoPolls,
    loggedIn: rSession.LOGGED_IN,
    exitLoading: rLoading.signOutLoading,
  };
}

export default connect(mapStateToProps, null)(memo(SidebarNav));
